import React, { useEffect, useMemo, useRef } from "react";
import { Autocomplete, TextField } from "@mui/material";
import AlertCircleFilled from "../alert-circle-filled.svg";
import Info from "../info-icon.svg";
import ArrowIcon from "../arrow.svg";

export const CsvMapper = ({
  fileColumns = [],
  mapping = {},
  setMapping,
  systemFields = [],
  mandatoryFields = [],
  sampleRow = {},
  deleteIcon,
}) => {
  const normalize = (str = "") =>
    String(str).toLowerCase().replace(/[\s_\-()]/g, "");
  const autoMapKey = useMemo(
    () =>
      `${fileColumns.join("|")}::${systemFields
        .map((field) => field?.label || field?.value || "")
        .join("|")}`,
    [fileColumns, systemFields]
  );
  const lastAutoMapKeyRef = useRef("");

  useEffect(() => {
    if (!fileColumns.length || !systemFields.length) return;
    if (lastAutoMapKeyRef.current === autoMapKey) return;

    lastAutoMapKeyRef.current = autoMapKey;

    const nextMappings = {};

    fileColumns.forEach((col) => {
      const colNorm = normalize(col);
      const match = systemFields
        .filter((f) => f?.label)
        .find((f) => normalize(f.label) === colNorm);

      if (match) {
        nextMappings[col] = match.value;
      }
    });
    if (Object.keys(nextMappings).length > 0) {
      setMapping((prev) => ({ ...prev, ...nextMappings }));
    }
  }, [autoMapKey, fileColumns, systemFields, setMapping]);

  const handleChange = (column, newValue) => {
    setMapping((prev) => ({
      ...prev,
      [column]: newValue?.value || "",
    }));
  };

  const unmappedCount = fileColumns.filter(
    (col) => !mapping[col]
  ).length;

  return (
    <div style={{ width: "100%" }}>
      {/* Warning */}
      {unmappedCount > 0 && (
        <div
          style={{
            background: "#FFF9F0",
            padding: "12px 16px",
            borderRadius: 8,
            fontSize: 13,
            marginBottom: 20,
            color: "#CC8D1E",
            fontWeight: 500,
            display: "flex",
            alignItems: "center",
            border: "1px solid #F7D7D3",
          }}
        >
          <img
            src={AlertCircleFilled}
            alt="alert"
            style={{ width: 16, height: 16, marginRight: 8 }}
          />
          <span>
            {unmappedCount} columns unmapped —{" "}
            <span style={{ fontWeight: 400 }}>
              This data will not get reflected into the platform
            </span>
          </span>
        </div>
      )}

      {/* Header Row */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 24px 1fr 1fr 24px",
          justifyContent: "space-between",
          gap: 16,
          fontWeight: 600,
          fontSize: 12,
          paddingBottom: 12,
          borderBottom: "1px solid #E5E7EB",
          marginBottom: 12,
          position: "sticky",
          top: 0,
          background: "#FFFFFF",
          zIndex: 1,
          color: "#424242",
        }}
      >
        <div>File Field/Columns</div>
        <div></div>
        <div>BambooBox Fields</div>
        <div>Sample Data</div>
        <div></div>
      </div>

      {/* Mapping Rows */}
      {fileColumns.map((column, index) => {
        const isUnmapped = !mapping[column];
        const isMandatory = false;

        return (
          <div
            key={column}
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 24px 1fr 1fr 24px",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 16,
              marginBottom: 16,
            }}
          >
            {/* CSV Column Box */}
            <div
              style={{
                fontSize: 13,
                fontWeight: 500,
                color: "#424242",
                border: "1px solid #EEEEEE",
                borderRadius: 6,
                padding: "0 14px",
                width: "100%",
                height: 36,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                boxSizing: "border-box",
                background: "#FFFFFF",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                {column}
              </div>
              {isUnmapped && (
                <img
                  src={Info}
                  alt="unmapped"
                  style={{ width: 14, height: 14 }}
                />
              )}
            </div>

            {/* Arrow */}
            <div
              style={{
                textAlign: "center",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <img src={ArrowIcon} alt="arrow" style={{ width: 12, height: 10 }} />
            </div>

            {/* Dropdown */}
            <div style={{ width: "100%" }}>
              <Autocomplete
                options={systemFields}
                getOptionLabel={(option) => option?.label || ""}
                isOptionEqualToValue={(option, value) =>
                  option.value === value.value
                }
                renderOption={(props, option) => (
                  <li {...props} key={option?.value || option?.label}>
                    {option?.label || ""}
                  </li>
                )}
                value={
                  systemFields.find((f) => f?.value === mapping[column]) || null
                }
                onChange={(e, newValue) => handleChange(column, newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    placeholder="Select Field"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        fontSize: "13px",
                        fontWeight: 500,
                        color: "rgb(66, 66, 66)",
                        border: "1px solid #E0E0E0",
                        borderRadius: "6px",
                        padding: "0 14px !important",
                        width: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        boxSizing: "border-box",
                        background: "rgb(255, 255, 255)",
                        minHeight: "36px",
                        transition: "background 0.2s ease",
                        "&:hover": {
                          background: "#F8FAFC",
                        },
                        "& fieldset": {
                          display: "none",
                        },
                      },
                      "& .MuiInputBase-input": {
                        padding: "0 !important",
                        fontSize: "13px",
                        fontWeight: 500,
                        color: "rgb(66, 66, 66)",
                      }
                    }}
                  />
                )}
              />
            </div>

            {/* Sample Data */}
            <div
              style={{
                fontSize: 13,
                color: "#424242",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                background: "#F8FAFC",
                borderRadius: 6,
                padding: "0 16px",
                width: "100%",
                height: 36,
                display: "flex",
                alignItems: "center",
                boxSizing: "border-box",
              }}
            >
              {sampleRow?.[column] || "-"}
            </div>

            {/* Delete */}
            <div style={{ display: "flex", justifyContent: "center" }}>
              {deleteIcon && (
                <img
                  src={deleteIcon}
                  alt="delete"
                  style={{
                    width: 24,
                    height: 24,
                    cursor: "pointer",
                    opacity: mapping[column] ? 1 : 0.35,
                    filter: mapping[column] ? "none" : "grayscale(100%)",
                  }}
                  onClick={() =>
                    setMapping((prev) => ({ ...prev, [column]: "" }))
                  }
                />
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};